import createAlgoliaHelper from '../../../utils/createAlgoliaHelper'
import fetchAllCategories from '../../../utils/fetchAllCategories'
import { FilterValues, Product } from '../types'
import config from '../../../config'
import * as t from '../types'
import getCookieByName from '../../../utils/getCookieByName'
import { isChannelB2B } from '../../../utils/channel'
import * as odoEvents from './odoscope-tracking'

type Result = {
  products: Product[]
  /** choose odoscope products when valid is true */
  valid: boolean
  /** must be refetched on client when not refined */
  refined: boolean
  oscTracking: string
  status:
    | 'success'
    | 'error'
    | 'timeout'
    | 'control'
    | 'empty'
    | 'prerender'
    | 'not-supported'
}

type OdoscopeResponse = {
  variants: { variantID: string }[]
  oscCookie: Record<string, string>
  oscTracking?: string
  goControl?: boolean
}

const indexDict: Record<t.Index, string> = {
  default: config.index.products,
  'price-asc': config.index.productsPriceAsc,
  latest: config.index.productsLatest,
  'price-desc': config.index.productsPriceDesc || ''
}

async function fetchFromOdoscopeInternal(
  filterValues: FilterValues,
  odoscopeCategoryPathId: string
): Promise<Result> {
  // it doesnot make sense to fetch from odoscope in the server side
  if (typeof window === 'undefined' || !config.modules.odoscope)
    return {
      products: [],
      valid: false,
      refined: false,
      status: 'prerender',
      oscTracking: ''
    }
  const categories = await fetchAllCategories()

  const category = categories.find((cat) => cat.path === filterValues.category)

  const url = new URL(config.modules.odoscope.url)
  const token = config.modules.odoscope.token

  url.searchParams.set('token', token)
  url.searchParams.set('limit', '24')
  url.searchParams.set('hostname', `${config.modules.odoscope.domain}/${config.locale}`)

  if (category) {
    url.searchParams.set('categoryID', odoscopeCategoryPathId)
  }

  url.searchParams.set('price_type', isChannelB2B() ? 'b2b' : 'b2c')

  const oscKey = `osc-${token}`
  const oscValue = getCookieByName(oscKey)
  if (oscValue) url.searchParams.set(oscKey, oscValue)

  /** we do not have this attribute in odoscope */
  if (filterValues.configurableProduct) {
    // url.searchParams.set('related.configurableRelations.type', 'tableColumn')
    // url.searchParams.set('related.configurableRelations.type', 'tableTop')
    return {
      products: [],
      valid: false,
      refined: true,
      status: 'not-supported',
      oscTracking: ''
    }
  }

  /**
   * odoscope cannot switch between distinct or not.
   * all skus from odoscope are distinct on the containerID and when we want
   * not to distinct we need to fetch from algolia
   */
  if (filterValues.distinct == false) {
    return {
      products: [],
      valid: false,
      refined: true,
      status: 'not-supported',
      oscTracking: ''
    }
  }

  // for(const flag of filterValues.flags) {
  //   url.searchParams.append('flags', flag)
  // }
  if (filterValues.flags.length > 0) {
    return {
      products: [],
      valid: false,
      refined: true,
      status: 'not-supported',
      oscTracking: ''
    }
  }

  /** we only need odoscope for relevance sorting (not eg price-ascending) */
  if (filterValues.index !== 'default') {
    return {
      products: [],
      valid: false,
      refined: true,
      status: 'not-supported',
      oscTracking: ''
    }
  }

  if (filterValues.minPrice) {
    url.searchParams.set('minPrice', filterValues.minPrice.toString())
  }

  if (filterValues.maxPrice) {
    url.searchParams.set('maxPrice', filterValues.maxPrice.toString())
  }

  // TODO: sellable filter (mode)

  if (filterValues.page) {
    url.searchParams.set('page', filterValues.page.toString())
  }

  if (filterValues.page) {
    url.searchParams.set('offset', (filterValues.page * 24).toString())
  }

  if (filterValues.productLine) {
    return {
      products: [],
      valid: false,
      refined: true,
      status: 'not-supported',
      oscTracking: ''
    }
  }

  if (filterValues.query) {
    return {
      products: [],
      valid: false,
      refined: true,
      status: 'not-supported',
      oscTracking: ''
    }
  }

  if (filterValues.skus.length > 0) {
    return {
      products: [],
      valid: false,
      refined: true,
      status: 'not-supported',
      oscTracking: ''
    }
  }

  for (const key in filterValues.numericAttributes) {
    const value = filterValues.numericAttributes[key]
    if (value.min || value.max) {
      return {
        products: [],
        valid: false,
        refined: true,
        status: 'not-supported',
        oscTracking: ''
      }
    }
  }

  for (const key in filterValues.attributes) {
    const values: string[] = []
    for (const value of filterValues.attributes[key]) {
      values.push(value)
    }
    if (values.length > 0) {
      url.searchParams.append(`cr_${key}`, values.join('|'))
    }
  }

  const data: OdoscopeResponse = await Promise.race([
    fetch(url.toString(), {
      headers: {
        'Content-Type': 'application/json'
      }
    }).then((response) => response.json()),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('timeout')), 1500)
    )
  ])

  // save osc-cookie
  if (data.oscCookie[oscKey]) {
    // max age is 30 days
    // on local environment we need to set the cookie on the root path because we do not have a proxy
    if (!location.pathname.includes(`/${config.locale}/`)) {
      document.cookie = `${oscKey}=${data.oscCookie[oscKey]}; path=/; max-age=2592000`
    } else {
      document.cookie = `${oscKey}=${data.oscCookie[oscKey]}; path=/${config.locale}/; max-age=2592000`
    }
  }

  const skus = data.variants.map((variant) => variant.variantID)

  if (data.goControl) {
    return {
      products: [],
      valid: false,
      refined: true,
      status: 'control',
      oscTracking: data.oscTracking || ''
    }
  }

  if (skus.length === 0) {
    return {
      products: [],
      valid: false,
      refined: true,
      status: 'empty',
      oscTracking: data.oscTracking || ''
    }
  }

  // TODO: research click analytics

  const helper = await createAlgoliaHelper(indexDict[filterValues.index], {
    facets: ['active', 'sellable'],
    clickAnalytics: false,
    enableABTest: false,
    disjunctiveFacets: ['sku'],
    distinct: 0,
    hitsPerPage: 24,
    attributesToRetrieve: [
      'sku',
      'containerID',
      'sw6Uuid',
      'prices',
      'priceRules',
      'images',
      'title',
      'channelActive',
      'subtitle',
      'variantData',
      'brand',
      'unit',
      'flags',
      'variantImages',
      'attributes',
      'categories',
      'stock',
      'deliveryDate',
      'deliveryDays',
      'specialDelivery',
      'sellOut',
      'sellable',
      'configurations',
      'isMeterware',
      'isMainVariant',
      'productReviews'
    ]
  })

  if (filterValues.mode === 'SELLABLE')
    helper.addFacetRefinement('sellable', 'true')

  if (filterValues.mode === 'ACTIVE')
    helper.addFacetRefinement('sellable', 'true')

  for (const sku of skus) {
    helper.addDisjunctiveFacetRefinement('sku', sku)
  }

  const result = await helper.searchOnce({})

  if (result.content.hits.length === 0) {
    return {
      products: [],
      valid: false,
      refined: true,
      status: 'empty',
      oscTracking: data.oscTracking || ''
    }
  }

  const hitDict = result.content.hits.reduce((acc, hit) => {
    acc[hit.sku] = hit
    return acc
  }, {})

  const products = skus.map((sku) => hitDict[sku]).filter(Boolean)

  return {
    products,
    valid: true,
    refined: true,
    status: 'success',
    oscTracking: data.oscTracking || ''
  }
}

export default async function fetchFromOdoscope(
  filterValues: FilterValues,
  odoscopeCategoryPathId: string
): Promise<Result> {
  if(typeof window === 'undefined') {
    return { products: [], valid: false, refined: false, oscTracking: '', status: 'prerender' }
  }
  try {
    const result = await fetchFromOdoscopeInternal(
      filterValues,
      odoscopeCategoryPathId
    )

    // odoscope event handling
    if (typeof window !== 'undefined') {
      if (result.status === 'not-supported') {
        odoEvents.unsupportedRequest()
      }
      if (result.status === 'control') {
        odoEvents.goControl()
      }
      if (result.status === 'empty') {
        odoEvents.emptyResponse(result.oscTracking)
      }
      if (result.status === 'success') {
        odoEvents.success(result.oscTracking)
      }
    }

    return result
  } catch (e: any) {
    if (typeof window !== 'undefined') {
      if (e.message === 'timeout') {
        odoEvents.requestTimeout()
      } else {
        odoEvents.requestError()
      }
    }
    // eslint-disable-next-line no-console
    console.error('Failed to fetch from odoscope', e)
    return {
      products: [],
      valid: false,
      refined: true,
      status: 'error',
      oscTracking: ''
    }
  }
}
